import { useMutation } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';
import type { CandidateDecision, SortByField } from 'hooks/useReviewTalentsCandidates';

interface DownloadCVsPayload {
  assessment_id: string;
  // Response IDs of the selected candidates.
  response_ids?: number[];
  decision?: CandidateDecision;
  search?: string;
  min_score?: number;
  max_score?: number;
  // Metrics the score range is scoped to, matching the list view's filter so a
  // "download all filtered CVs" zip holds exactly the rows on screen.
  metrics?: string[];
  sort_by?: SortByField;
  sort_order?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
}

const extractFilename = (contentDisposition?: string): string => {
  if (!contentDisposition) return 'candidate_cvs.zip';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition);
  return match?.[1] ? decodeURIComponent(match[1]) : 'candidate_cvs.zip';
};

/**
 * Downloads the selected candidates' CVs as a single zip file.
 * Replaces the previous one-file-at-a-time download flow.
 */
export const useDownloadCVs = () => {
  const { openToast } = useToast();

  return useMutation({
    mutationFn: async ({
      assessment_id,
      response_ids,
      decision,
      search,
      min_score,
      max_score,
      metrics,
      sort_by,
      sort_order,
      page,
      page_size,
    }: DownloadCVsPayload) => {
      const params: Record<string, string | number | number[]> = {
        assessment_id,
      };

      if (response_ids && response_ids.length > 0) {
        params.response_ids = response_ids;
      }
      if (decision) params.decision = decision;
      if (search?.trim()) params.search = search.trim();
      if (min_score !== undefined) params.min_score = min_score;
      if (max_score !== undefined) params.max_score = max_score;
      if (metrics?.length) params.metrics = metrics.join(',');
      if (sort_by) params.sort_by = sort_by;
      if (sort_order) params.sort_order = sort_order;
      if (page !== undefined) params.page = page;
      if (page_size !== undefined) params.page_size = page_size;

      const response = await apiClient.get('/candidate/dashboard/download-cvs/', {
        params,
        // Serialize arrays as repeated keys (response_ids=1&response_ids=2) so
        // the backend can read them with getlist().
        paramsSerializer: { indexes: null },
        responseType: 'blob',
      });
      return {
        blob: response.data as Blob,
        filename: extractFilename(response.headers['content-disposition']),
      };
    },
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to download CVs';
      openToast({
        show: true,
        heading: 'Error',
        text: errorMessage,
        type: 'error',
        timeOut: 5000,
      });
    },
  });
};
